/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'

import {
  ExpandableOverviewItem,
  SpanWithData,
  KeyValueTable,
  CurrencyInput,
  Spinner
} from '../'

import { CalculationParameters } from './CalculationParameters'

import { CalculationTermComparison } from './CalculationTermComparison'

import { showAsCurrency } from '../../utils'


const QUERY_CREDITAPPLICATION_CALCULATIONS = gql`
  query($id: ID!) {
    creditapplication(id: $id) {
      id
      calcShortest
      calcLongest
      calcActual
      overrideShortest
      overrideLongest
      overrideActual
      repaymentMaxApplied
      borrowableMax
      creditMaxTerm
      productBorrowableActual
      eligibleLoanAmount
      overrideBorrowableMax,
      overrideProductBorrowableActual
      overrideEligibleLoanAmount
      eligibleLowerDescription
      overrideEligibleLowerDescription
    }
  }
`


export class CreditCalculations extends React.Component {
  static propTypes = {
    creditApplication: PropTypes.shape({
      id: PropTypes.string.isRequired,
      amountAppliedFor: PropTypes.string,
      repaymentMaxApplied: PropTypes.string,
      borrowableMax: PropTypes.string,
      productBorrowableActual: PropTypes.string,
      eligibleLoanAmount: PropTypes.string,
      eligibleLowerDescription: PropTypes.string,
      warningUnevenDeductions: PropTypes.string,
      updatedBy: PropTypes.shape({
        id: PropTypes.string.isRequired,
        identity: PropTypes.shape({
          id: PropTypes.string.isRequired,
          fullName: PropTypes.string.isRequired
        })
      }).isRequired,
      overrideBy: PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired
      }),
      overrideAt: PropTypes.string,
      creditType: PropTypes.string.isRequired
    }).isRequired,
    open: PropTypes.bool.isRequired,
    calculationsLocked: PropTypes.bool,
    calculationsSnapshot: PropTypes.string,
    updateCreditApplication: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    cashAppliedForEligible: null,
    retailAppliedForEligible: null,
    consolidationAppliedForEligible: null,
    showOverride: this.props.userHasOverrideResponsibility,
    // overrideDeductions: this.props.creditApplication.overrideDeductions,  // NOTE: for coming version
    overrideBorrowableMax: null,
    overrideProductBorrowableActual: this.props.creditApplication.overrideProductBorrowableActual,
    overrideEligibleLoanAmount: this.props.creditApplication.overrideEligibleLoanAmount,
    overrideInProgress: false
  }

  showSummary = (creditType, amountAppliedFor, amountEligibleFor) => (
    <Fragment>
      {`Applied for ${showAsCurrency('R', amountAppliedFor)} `}
      (<SpanWithData forField='CreditCreditType' searchKey={creditType} />)
      {`, eligible for ${showAsCurrency('R', amountEligibleFor)}`}
    </Fragment>
  )

  handleInputChange = (e) => {
    const field = e.target.id

    this.setState({ [field]: e.target.value })
  }

  requestOverride = () => {
    this.props.updateCreditApplication({ overrideStatus: 'requested' })
      .then(() => this.setState({ showOverride: this.props.userHasOverrideResponsibility }))
      .catch(error => Sentry.captureMessage(`Override request initiation failed - ${error}`))
  }

  saveOverride = () => {
    this.setState({ overrideInProgress: true })

    let input = {
      overrideStatus: 'completed'
    }

    if (this.overrideBorrowableMaxChanged()) {
      input.overrideBorrowableMax = this.state.overrideBorrowableMax
    }

    this.props.updateCreditApplication(input)
      .then(({ data }) => {
        this.setState({
          showOverride: this.props.userHasOverrideResponsibility,
          overrideInProgress: false
        })
      })
      .catch(error => {
        Sentry.captureMessage(`Saving of credit application calculation fields failed - ${error}`)
        this.setState({ overrideInProgress: false })
      })
  }

  overrideBorrowableMaxChanged = (overrideBorrowableMax) => (
    this.state.overrideBorrowableMax
      ? parseFloat(this.state.overrideBorrowableMax) !== overrideBorrowableMax
      : false
  )

  toggleOverrideInProgress = () => this.setState(prevState => ({ overrideInProgress: !prevState.overrideInProgress }))

  render() {
    const { client, open, calculationsLocked, calculationsSnapshot, onClick, className, updateCreditApplication } = this.props
    const { amountAppliedFor, creditType, calcTerm, overrideTerm, overrideStatus, locked } = this.props.creditApplication

    const overrideCompleted = overrideStatus === 'COMPLETED'
    const overrideRequested = overrideStatus === 'REQUESTED'
    const overridePerformed = overrideStatus !== null

    if (calculationsLocked) {
      const calculations = JSON.parse(calculationsSnapshot)
      const { repaymentMaxApplied, borrowableMax, productBorrowableActual, eligibleLoanAmount, overrideBorrowableMax, creditMaxTerm,
        overrideProductBorrowableActual, overrideEligibleLoanAmount, eligibleLowerDescription, overrideEligibleLowerDescription } = calculations

      const calcShortest = calculations.calcShortest
      const calcLongest = calculations.calcLongest
      const calcActual = calculations.calcActual
      const calcActualWithTerm = { ...calcActual, ...{ term: calcTerm } }  // adds term property to calcActual object

      const overrideShortest = calculations.overrideShortest
      const overrideLongest = calculations.overrideLongest
      const overrideActual = calculations.overrideActual
      const overrideActualWithTerm = { ...overrideActual, ...{ term: overrideTerm } }
      const clientEligibleForLoan = calcShortest && calcShortest['1. Term'] >= 0

      let originalParameters = {
        'Loan amount applied for': showAsCurrency('R', amountAppliedFor, 2, true),
        'Max monthly repayment': showAsCurrency('R', repaymentMaxApplied, 2, true),
        'Max borrowable': showAsCurrency('R', borrowableMax, 2, true),
        'Max borrowable for this product': showAsCurrency('R', productBorrowableActual, 2, true),
        'Eligible loan amount': showAsCurrency('R', eligibleLoanAmount, 2, true)
      }

      let overrideParameters = {
        ...originalParameters,
        ...{
          'Max borrowable': this.state.showOverride && !locked && clientEligibleForLoan
            ? <CurrencyInput
                id='overrideBorrowableMax'
                value={this.state.overrideBorrowableMax}
                onChange={this.handleInputChange}
                minValue={0}
                maxWidth={125}
                className='slds-float_right'
              />
            : overridePerformed
              ? showAsCurrency('R', overrideBorrowableMax, 2, true)
              : showAsCurrency('R', borrowableMax, 2, true),
          'Max borrowable for this product': this.state.overrideInProgress
            ? <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
            : overridePerformed
              ? showAsCurrency('R', overrideProductBorrowableActual, 2, true)
              : showAsCurrency('R', productBorrowableActual, 2, true),
          'Eligible loan amount': this.state.overrideInProgress
            ? <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
            : overridePerformed
              ? showAsCurrency('R', overrideEligibleLoanAmount, 2, true)
              : showAsCurrency('R', eligibleLoanAmount, 2, true)
        }
      }

      if (this.state.overrideInProgress) {
        overrideParameters['Reason for lower loan amount than applied for'] = <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
      }

      if (overridePerformed && overrideEligibleLoanAmount < amountAppliedFor) {
        overrideParameters['Reason for lower loan amount than applied for'] = <span className='slds-text-body_small'>{overrideEligibleLowerDescription}</span>
      }

      if (eligibleLoanAmount < amountAppliedFor) {
        originalParameters['Reason for lower loan amount than applied for'] = <span className='slds-text-body_small'>{eligibleLowerDescription}</span>
      }

      originalParameters['Client payroll cycle'] = <SpanWithData forField='PayrollCycleCycle' searchKey={client.payrollcycle.cycle} />
      overrideParameters['Client payroll cycle'] = <SpanWithData forField='PayrollCycleCycle' searchKey={client.payrollcycle.cycle} />

      return (
        <ExpandableOverviewItem
          title='Calculations'
          summary={
            this.showSummary(
              creditType,
              amountAppliedFor,
              overridePerformed && overrideEligibleLoanAmount
                ? overrideEligibleLoanAmount
                : eligibleLoanAmount
            )
          }
          iconSymbol={clientEligibleForLoan
            ? 'check'
            : 'error'
          }
          iconColour={clientEligibleForLoan
            ? 'success'
            : 'error'
          }
          open={open}
          onClick={onClick}
          className={className}>

          {(this.state.showOverride && !locked) || overridePerformed
            ? <CalculationParameters
                originalParameters={originalParameters}
                overrideParameters={overrideParameters}
                overrideInProgress={this.state.overrideInProgress}
                handleSaveOverride={this.saveOverride}
                overrideBorrowableMaxChanged={false}
                clientEligibleForLoan={clientEligibleForLoan}
              />
            : <KeyValueTable
                heading={null}
                data={originalParameters}
                keyColumnSize="2-of-3"
                className='slds-m-bottom_medium'
                tableClassName='slds-no-row-hover table-cells-align_right'
              />
          }
          {(eligibleLoanAmount && parseFloat(eligibleLoanAmount) > 0) ||
            (overrideEligibleLoanAmount && parseFloat(overrideEligibleLoanAmount) > 0)

            ? <CalculationTermComparison
                showOverride={this.state.showOverride}
                overridePerformed={overridePerformed}
                overrideShortest={overrideShortest}
                overrideLongest={overrideLongest}
                overrideActualWithTerm={overrideActualWithTerm}
                overrideCompleted={overrideCompleted}
                overrideRequested={overrideRequested}
                calcShortest={calcShortest}
                calcLongest={calcLongest}
                calcActualWithTerm={calcActualWithTerm}
                locked={locked}
                handleRequestOverride={this.requestOverride}
                clientEligibleForLoan={clientEligibleForLoan}
                overrideStatus={overrideStatus}
                overrideTerm={overrideTerm}
                calcTerm={calcTerm}
                creditMaxTerm={creditMaxTerm}
                updateCreditApplication={updateCreditApplication}
                overrideInProgress={this.state.overrideInProgress}
                toggleOverrideInProgress={this.toggleOverrideInProgress}
              />
            : null
          }

        </ExpandableOverviewItem>
      )
    } else {
      return (
        <Query
          query={QUERY_CREDITAPPLICATION_CALCULATIONS}
          variables={{ id: this.props.creditApplication.id}}
          fetchPolicy='network-only'>

          {({ loading, error, data }) => {
            if (loading || error) {
              return (
                <ExpandableOverviewItem
                  title='Calculations'
                  summary={<Spinner size='small' type='brand' />}
                  iconSymbol='info'
                  iconColour='light'
                  locked={true}
                  onClick={onClick}
                  className={className}
                />
              )
            }

            const { repaymentMaxApplied, borrowableMax, productBorrowableActual, eligibleLoanAmount, overrideBorrowableMax, creditMaxTerm,
              overrideProductBorrowableActual, overrideEligibleLoanAmount, eligibleLowerDescription, overrideEligibleLowerDescription } = data.creditapplication

            const calcShortest = JSON.parse(data.creditapplication.calcShortest)
            const calcLongest = JSON.parse(data.creditapplication.calcLongest)
            const calcActual = JSON.parse(data.creditapplication.calcActual)
            const calcActualWithTerm = { ...calcActual, ...{ term: calcTerm } }  // adds term property to calcActual object

            const overrideShortest = JSON.parse(data.creditapplication.overrideShortest)
            const overrideLongest = JSON.parse(data.creditapplication.overrideLongest)
            const overrideActual = JSON.parse(data.creditapplication.overrideActual)
            const overrideActualWithTerm = { ...overrideActual, ...{ term: overrideTerm } }
            const clientEligibleForLoan = calcShortest['1. Term'] >= 0

            let originalParameters = {
              'Loan amount applied for': showAsCurrency('R', amountAppliedFor, 2, true),
              'Max monthly repayment': showAsCurrency('R', repaymentMaxApplied, 2, true),
              'Max borrowable': showAsCurrency('R', borrowableMax, 2, true),
              'Max borrowable for this product': showAsCurrency('R', productBorrowableActual, 2, true),
              'Eligible loan amount': showAsCurrency('R', eligibleLoanAmount, 2, true)
            }

            let overrideParameters = {
              ...originalParameters,
              ...{
                'Max borrowable': this.state.showOverride && !locked && clientEligibleForLoan
                  ? <CurrencyInput
                      id='overrideBorrowableMax'
                      value={this.state.overrideBorrowableMax}
                      onChange={this.handleInputChange}
                      minValue={0}
                      maxWidth={125}
                      className='slds-float_right'
                    />
                  : overridePerformed
                    ? showAsCurrency('R', overrideBorrowableMax, 2, true)
                    : showAsCurrency('R', borrowableMax, 2, true),
                'Max borrowable for this product': this.state.overrideInProgress
                  ? <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
                  : overridePerformed
                    ? showAsCurrency('R', overrideProductBorrowableActual, 2, true)
                    : showAsCurrency('R', productBorrowableActual, 2, true),
                'Eligible loan amount': this.state.overrideInProgress
                  ? <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
                  : overridePerformed
                    ? showAsCurrency('R', overrideEligibleLoanAmount, 2, true)
                    : showAsCurrency('R', eligibleLoanAmount, 2, true)
              }
            }

            if (this.state.overrideInProgress) {
              overrideParameters['Reason for lower loan amount than applied for'] = <Spinner size='x-small' className='slds-is-relative slds-float_right slds-m-right_medium' />
            }

            if (overridePerformed && overrideEligibleLoanAmount < amountAppliedFor) {
              overrideParameters['Reason for lower loan amount than applied for'] = <span className='slds-text-body_small'>{overrideEligibleLowerDescription}</span>
            }

            if (eligibleLoanAmount < amountAppliedFor) {
              originalParameters['Reason for lower loan amount than applied for'] = <span className='slds-text-body_small'>{eligibleLowerDescription}</span>
            }

            originalParameters['Client payroll cycle'] = <SpanWithData forField='PayrollCycleCycle' searchKey={client.payrollcycle.cycle} />
            overrideParameters['Client payroll cycle'] = <SpanWithData forField='PayrollCycleCycle' searchKey={client.payrollcycle.cycle} />

            return (
              <ExpandableOverviewItem
                title='Calculations'
                summary={
                  this.showSummary(
                    creditType,
                    amountAppliedFor,
                    overridePerformed && overrideEligibleLoanAmount
                      ? overrideEligibleLoanAmount
                      : eligibleLoanAmount
                  )
                }
                iconSymbol={clientEligibleForLoan
                  ? 'check'
                  : 'error'
                }
                iconColour={clientEligibleForLoan
                  ? 'success'
                  : 'error'
                }
                open={open}
                onClick={onClick}
                className={className}>

                {(this.state.showOverride && !locked) || overridePerformed
                  ? <CalculationParameters
                      originalParameters={originalParameters}
                      overrideParameters={overrideParameters}
                      overrideInProgress={this.state.overrideInProgress}
                      handleSaveOverride={this.saveOverride}
                      overrideBorrowableMaxChanged={this.overrideBorrowableMaxChanged(overrideBorrowableMax)}
                      clientEligibleForLoan={clientEligibleForLoan}
                    />
                  : <KeyValueTable
                      heading={null}
                      data={originalParameters}
                      keyColumnSize="2-of-3"
                      className='slds-m-bottom_medium'
                      tableClassName='slds-no-row-hover table-cells-align_right'
                    />
                }
                {(eligibleLoanAmount && parseFloat(eligibleLoanAmount) > 0) ||
                  (overrideEligibleLoanAmount && parseFloat(overrideEligibleLoanAmount) > 0)

                  ? <CalculationTermComparison
                      showOverride={this.state.showOverride}
                      overridePerformed={overridePerformed}
                      overrideShortest={overrideShortest}
                      overrideLongest={overrideLongest}
                      overrideActualWithTerm={overrideActualWithTerm}
                      overrideCompleted={overrideCompleted}
                      overrideRequested={overrideRequested}
                      calcShortest={calcShortest}
                      calcLongest={calcLongest}
                      calcActualWithTerm={calcActualWithTerm}
                      locked={locked}
                      handleRequestOverride={this.requestOverride}
                      clientEligibleForLoan={clientEligibleForLoan}
                      overrideStatus={overrideStatus}
                      overrideTerm={overrideTerm}
                      calcTerm={calcTerm}
                      creditMaxTerm={creditMaxTerm}
                      updateCreditApplication={updateCreditApplication}
                      overrideInProgress={this.state.overrideInProgress}
                      toggleOverrideInProgress={this.toggleOverrideInProgress}
                    />
                  : null
                }

              </ExpandableOverviewItem>
            )
          }}
        </Query>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-modal/CreditCalculations.js